// ============================================================================
//  Projects Module — Pages
//  ProjectsPage · ProjectBoardPage · ProjectDetailPage
// ============================================================================

import React, { useState, useRef, useCallback } from 'react'
import { usersApi } from '@/modules/users/api/users.api'
import ReactDOM from 'react-dom'
import api from '@/lib/axios'
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts'
import { ListPageSkeleton, KanbanSkeleton, CardSkeleton } from '@/components/ui/Skeleton'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  FolderKanban,
  Plus,
  Search,
  MoreVertical,
  ChevronLeft,
  Calendar,
  Users,
  CheckCircle2,
  Clock,
  AlertTriangle,
  GripVertical,
  X,
  Edit2,
  Trash2,
  Flag,
  User,
  Timer,
  MessageSquare,
  Paperclip,
  Play,
  Square,
  ArrowRight,
  BarChart3,
  Zap,
  Circle,
  CheckCircle,
  Tag,
  Settings,
  TrendingDown,
} from 'lucide-react'
import {
  projectsApi,
  type Project,
  type Task,
  type TaskStatus,
  type Sprint,
  type TimeLog,
  type ProjectLabel,
} from '@/modules/projects/api/projects.api'
import { useNotificationStore } from '@/stores/notification.store'
import { useAuthStore } from '@/stores/auth.store'

// ─── Helpers ─────────────────────────────────────────────────────────────────

const fmtDate = (d?: string) =>
  d ? new Date(d).toLocaleDateString('fr-SN', { day: '2-digit', month: 'short' }) : '—'
const fmtFull = (d?: string) =>
  d
    ? new Date(d).toLocaleDateString('fr-SN', { day: '2-digit', month: 'short', year: 'numeric' })
    : '—'

const PROJECT_STATUS: Record<string, { label: string; color: string; bg: string }> = {
  PLANNING: {
    label: 'Planification',
    color: 'text-[var(--tx3)]',
    bg: 'bg-[#D6D2EC]/10 border-[var(--tx3)]/20',
  },
  ACTIVE: {
    label: 'En cours',
    color: 'text-[var(--blue-d)]',
    bg: 'bg-[#E0F2FE] border-[rgba(245,124,0,0.25)]',
  },
  ON_HOLD: {
    label: 'En pause',
    color: 'text-[#D97706]',
    bg: 'bg-[#FEF3C7] border-[rgba(245,158,11,0.25)]',
  },
  COMPLETED: {
    label: 'Terminé',
    color: 'text-[#059669]',
    bg: 'bg-[#10B981]/10 border-[rgba(16,185,129,0.25)]',
  },
  CANCELLED: {
    label: 'Annulé',
    color: 'text-[#DC2626]',
    bg: 'bg-[#FEF2F2] border-[rgba(239,68,68,0.25)]',
  },
}

const PRIORITY: Record<string, { label: string; color: string; icon: React.ElementType }> = {
  LOW: { label: 'Faible', color: 'text-[var(--tx3)]', icon: Circle },
  MEDIUM: { label: 'Moyen', color: 'text-[var(--blue-d)]', icon: Circle },
  HIGH: { label: 'Élevé', color: 'text-[#D97706]', icon: Zap },
  URGENT: { label: 'Urgent', color: 'text-[#DC2626]', icon: AlertTriangle },
}

function Avatar({
  user,
  size = 'sm',
}: {
  user?: { firstName: string; lastName: string; avatarUrl?: string }
  size?: 'xs' | 'sm' | 'md'
}) {
  if (!user) return <div className="w-7 h-7 rounded-full bg-[var(--surface-2)] border border-[var(--bd)]" />
  const s = { xs: 'w-5 h-5 text-[10px]', sm: 'w-7 h-7 text-xs', md: 'w-9 h-9 text-sm' }[size]
  const initials = `${user.firstName[0]}${user.lastName[0]}`
  return user.avatarUrl ? (
    <img src={user.avatarUrl} alt={initials} className={`${s} rounded-full object-cover`} />
  ) : (
    <div
      className={`${s} rounded-full bg-gradient-to-br from-primary-600 to-primary-500 flex items-center justify-center text-[var(--brand-dark)] font-bold shrink-0`}
    >
      {initials}
    </div>
  )
}

function StatusBadge({ status }: { status: string }) {
  const cfg = PROJECT_STATUS[status] ?? PROJECT_STATUS.PLANNING
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${cfg.bg} ${cfg.color}`}
    >
      {cfg.label}
    </span>
  )
}

function PriorityIcon({ priority }: { priority: string }) {
  const cfg = PRIORITY[priority] ?? PRIORITY.MEDIUM
  return <cfg.icon className={`w-3.5 h-3.5 ${cfg.color}`} />
}

function Spinner() {
  return (
    <div className="w-5 h-5 border-2 border-[var(--blue)] border-t-transparent rounded-full animate-spin" />
  )
}

// ============================================================================
//  PROJECTS LIST PAGE
// ============================================================================

export default function ProjectsPage() {
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('')
  const [showCreate, setShowCreate] = useState(false)
  const addNotification = useNotificationStore((s) => s.addNotification)
  const queryClient = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: ['projects', { search, status }],
    queryFn: () => projectsApi.list({ search: search || undefined, status: status || undefined }),
  })

  const projects: Project[] = data?.data ?? data?.projects ?? (Array.isArray(data) ? data : [])
  const stats = data?.stats

  return (
    <div className="space-y-5 nx-fade-up">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[var(--brand-dark)]">Projets</h1>
          <p className="text-sm text-[var(--tx3)] mt-0.5">
            {projects.length} projet{projects.length > 1 ? 's' : ''}
          </p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-2 px-4 py-2 bg-[var(--blue)] hover:bg-[var(--blue-d)] text-white rounded-lg text-sm font-medium transition-colors"
        >
          <Plus className="w-4 h-4" /> Nouveau projet
        </button>
      </div>

      {/* KPIs */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 nx-stagger">
          {[
            {
              label: 'En cours',
              value: stats.active,
              icon: Play,
              color: 'bg-[#E0F2FE] text-[var(--blue-d)]',
            },
            {
              label: 'Terminés',
              value: stats.completed,
              icon: CheckCircle,
              color: 'bg-[#10B981]/10 text-[#059669]',
            },
            {
              label: 'En pause',
              value: stats.onHold,
              icon: Square,
              color: 'bg-[#FEF3C7] text-[#D97706]',
            },
            {
              label: 'Planifiés',
              value: stats.planning,
              icon: Calendar,
              color: 'bg-[#D6D2EC]/10 text-[var(--tx3)]',
            },
          ].map((k) => (
            <div
              key={k.label}
              className="bg-white border border-[var(--bd)] rounded-xl p-5 flex items-center gap-4 shadow-sm hover:shadow-md transition-shadow"
              style={{
                borderTop: `3px solid ${k.color.match(/#[0-9A-Fa-f]{3,6}/g)?.[0] ?? 'var(--brand-orange)'}`,
              }}
            >
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${k.color}`}>
                <k.icon className="w-6 h-6" />
              </div>
              <div>
                <p className="text-xl font-bold text-[var(--brand-dark)]">{k.value ?? 0}</p>
                <p className="text-xs text-[var(--tx3)]">{k.label}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Filtres */}
      <div className="flex gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--tx3)]" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher un projet…"
            className="w-full pl-9 pr-4 py-2 bg-white border border-[var(--bd)] rounded-lg text-sm text-[var(--brand-dark)] placeholder:text-[var(--tx3)] focus:outline-none focus:ring-1 focus:ring-[var(--blue)]"
          />
        </div>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="px-3 py-2 bg-white border border-[var(--bd)] rounded-lg text-sm text-[var(--brand-dark)] focus:outline-none focus:ring-1 focus:ring-[var(--blue)]"
        >
          <option value="">Tous les statuts</option>
          {Object.entries(PROJECT_STATUS).map(([k, v]) => (
            <option key={k} value={k}>
              {v.label}
            </option>
          ))}
        </select>
      </div>

      {/* Grid projets */}
      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">{[...Array(6)].map((_,i)=><CardSkeleton key={i} lines={3}/>)}</div>
      ) : projects.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-10 text-[var(--tx3)]">
          <FolderKanban className="w-10 h-10 mb-3 opacity-30" />
          <p className="text-sm">Aucun projet trouvé</p>
          <button
            onClick={() => setShowCreate(true)}
            className="mt-3 text-xs text-[var(--blue-d)] hover:text-blue-300"
          >
            Créer votre premier projet →
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {projects.map((p) => (
            <Link
              key={p.id}
              to={`/projects/${p.id}/board`}
              className="group bg-[var(--bg)] border border-[var(--bd)] rounded-xl p-5 hover:border-blue-500/30 hover:bg-[var(--surface-2)] transition-all"
            >
              {/* Couleur + nom */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div
                    className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                    style={{
                      background: p.color ? `${p.color}20` : '#3b82f620',
                      border: `1px solid ${p.color ?? '#3b82f6'}30`,
                    }}
                  >
                    <FolderKanban className="w-4 h-4" style={{ color: p.color ?? '#3b82f6' }} />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-[var(--brand-dark)] group-hover:text-blue-300 transition-colors line-clamp-1">
                      {p.name}
                    </p>
                    <StatusBadge status={p.status} />
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <PriorityIcon priority={p.priority} />
                  <Link
                    to={`/projects/${p.id}`}
                    onClick={(e) => e.stopPropagation()}
                    className="p-1 rounded hover:bg-[var(--bd)] text-[var(--tx3)] hover:text-[var(--brand-dark)] transition-colors"
                    title="Paramètres du projet"
                  >
                    <Settings className="w-3.5 h-3.5" />
                  </Link>
                </div>
              </div>

              {/* Description */}
              {p.description && (
                <p className="text-xs text-[var(--tx3)] mb-4 line-clamp-2">{p.description}</p>
              )}

              {/* Progression */}
              <div className="mb-4">
                <div className="flex justify-between text-xs text-[var(--tx3)] mb-1">
                  <span>Progression</span>
                  <span>{p.progress}%</span>
                </div>
                <div className="w-full h-1.5 bg-[var(--surface-2)] rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all"
                    style={{ width: `${p.progress}%`, background: p.color ?? '#3b82f6' }}
                  />
                </div>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 text-xs text-[var(--tx3)]">
                  <span className="flex items-center gap-1">
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    {p._count.tasks} tâches
                  </span>
                  <span className="flex items-center gap-1">
                    <Users className="w-3.5 h-3.5" />
                    {p._count.members}
                  </span>
                </div>
                {p.dueDate && (
                  <span
                    className={`flex items-center gap-1 text-xs ${new Date(p.dueDate) < new Date() && p.status !== 'COMPLETED' ? 'text-[#DC2626]' : 'text-[var(--tx3)]'}`}
                  >
                    <Calendar className="w-3 h-3" />
                    {fmtDate(p.dueDate)}
                  </span>
                )}
              </div>

              {/* Membres */}
              {p.members.length > 0 && (
                <div className="flex items-center mt-3 -space-x-2">
                  {p.members.slice(0, 5).map((m) => (
                    <Avatar key={m.id} user={m.user} size="xs" />
                  ))}
                  {p.members.length > 5 && (
                    <div className="w-5 h-5 rounded-full bg-[var(--surface-2)] border border-[var(--bd)] flex items-center justify-center text-[10px] text-[var(--tx3)]">
                      +{p.members.length - 5}
                    </div>
                  )}
                </div>
              )}
            </Link>
          ))}
        </div>
      )}

      {/* Modal création */}
      {showCreate && (
        <CreateProjectModal
          onClose={() => setShowCreate(false)}
          onCreated={(p) => {
            queryClient.invalidateQueries({ queryKey: ['projects'] })
            addNotification({ type: 'success', title: 'Projet créé', message: p.name })
            setShowCreate(false)
          }}
        />
      )}
    </div>
  )
}

// ─── Create Project Modal ─────────────────────────────────────────────────────

function CreateProjectModal({
  onClose,
  onCreated,
}: {
  onClose: () => void
  onCreated: (p: Project) => void
}) {
  const [form, setForm] = useState({
    name: '',
    key: '',
    description: '',
    coverColor: '#3b82f6',
    priority: 'MEDIUM',
    endDate: '',
  })
  const set = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }))

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      projectsApi.create({
        ...form,
        key:
          form.key ||
          form.name
            .toUpperCase()
            .replace(/[^A-Z0-9]/g, '')
            .slice(0, 8) ||
          'PROJ',
        endDate: form.endDate || undefined,
      }),
    onSuccess: onCreated,
  })

  const COLORS = ['#3b82f6', '#8b5cf6', '#10b981', '#f59e0b', '#ef4444', '#06b6d4', '#ec4899']

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-[rgba(13,8,32,0.50)] backdrop-blur-sm flex items-center justify-center z-[99999] p-4">
      <div className="bg-white border border-[var(--bd)] rounded-2xl w-full max-w-2xl shadow-2xl nx-scale-in">
        <div className="flex items-center justify-between p-5 border-b border-[var(--bd)]">
          <h2 className="text-sm font-semibold text-[var(--tx2)]">Nouveau projet</h2>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-[var(--bd)] rounded-lg transition-colors"
          >
            <X className="w-4 h-4 text-[var(--tx3)]" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-xs font-medium text-[var(--tx3)] mb-1.5">
              Nom <span className="text-[#DC2626]">*</span>
            </label>
            <input
              value={form.name}
              onChange={(e) => set('name', e.target.value)}
              placeholder="Nom du projet…"
              className="w-full px-3 py-2 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm text-[var(--brand-dark)] focus:outline-none focus:ring-1 focus:ring-[var(--blue)]"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-[var(--tx3)] mb-1.5">Description</label>
            <textarea
              value={form.description}
              onChange={(e) => set('description', e.target.value)}
              rows={2}
              placeholder="Description optionnelle…"
              className="w-full px-3 py-2 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm text-[var(--brand-dark)] focus:outline-none focus:ring-1 focus:ring-[var(--blue)] resize-none"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-[var(--tx3)] mb-1.5">Priorité</label>
              <select
                value={form.priority}
                onChange={(e) => set('priority', e.target.value)}
                className="w-full px-3 py-2 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm text-[var(--brand-dark)] focus:outline-none focus:ring-1 focus:ring-[var(--blue)]"
              >
                {Object.entries(PRIORITY).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--tx3)] mb-1.5">Échéance</label>
              <input
                type="date"
                value={form.endDate}
                onChange={(e) => set('endDate', e.target.value)}
                className="w-full px-3 py-2 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm text-[var(--brand-dark)] focus:outline-none focus:ring-1 focus:ring-[var(--blue)]"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-[var(--tx3)] mb-2">Couleur</label>
            <div className="flex gap-2">
              {COLORS.map((c) => (
                <button
                  key={c}
                  onClick={() => set('coverColor', c)}
                  className={`w-7 h-7 rounded-full transition-all ${form.coverColor === c ? 'ring-2 ring-[var(--brand-orange)] ring-offset-2 ring-offset-white scale-110' : 'hover:scale-110'}`}
                  style={{ background: c }}
                />
              ))}
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end gap-3 p-5 border-t border-[var(--bd)]">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-[var(--tx3)] hover:text-[var(--brand-dark)] transition-colors"
          >
            Annuler
          </button>
          <button
            onClick={() => mutate()}
            disabled={isPending || !form.name}
            className="flex items-center gap-2 px-5 py-2 bg-[var(--blue)] hover:bg-[var(--blue-d)] text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
          >
            {isPending ? <Spinner /> : <Plus className="w-4 h-4" />} Créer
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}

// ============================================================================
//  BURNDOWN CHART
// ============================================================================

function BurndownChart({ projectId, sprintId, sprintName }: { projectId: string; sprintId: string; sprintName: string }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['burndown', projectId, sprintId],
    queryFn: () => projectsApi.getBurndown(projectId, sprintId),
    staleTime: 5 * 60 * 1000,
  })

  if (isLoading) return (
    <div className="flex items-center justify-center h-40 bg-white border border-[var(--bd)] rounded-xl">
      <div className="w-5 h-5 border-2 border-[var(--blue)] border-t-transparent rounded-full animate-spin" />
    </div>
  )

  if (isError || !data?.days?.length) return (
    <div className="flex items-center justify-center h-40 bg-white border border-[var(--bd)] rounded-xl text-sm text-[var(--tx3)]">
      Données insuffisantes pour afficher le burndown.
    </div>
  )

  const { days, totalPoints, velocity } = data
  const chartData = days.map((d: any) => ({
    date: new Date(d.date).toLocaleDateString('fr-SN', { day: '2-digit', month: 'short' }),
    Idéal: d.ideal,
    Réel: d.actual ?? undefined,
  }))

  return (
    <div className="bg-white border border-[var(--bd)] rounded-xl p-4">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm font-semibold text-[var(--brand-dark)]">Burndown — {sprintName}</p>
        <div className="flex items-center gap-4 text-xs text-[var(--tx3)]">
          <span>{totalPoints} pts total</span>
          {velocity != null && <span>Vélocité : {velocity}</span>}
        </div>
      </div>
      <ResponsiveContainer width="100%" height={200}>
        <LineChart data={chartData} margin={{ top: 4, right: 12, bottom: 0, left: -20 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--bd)" />
          <XAxis dataKey="date" tick={{ fontSize: 10, fill: 'var(--tx3)' }} />
          <YAxis tick={{ fontSize: 10, fill: 'var(--tx3)' }} />
          <Tooltip
            contentStyle={{ background: '#fff', border: '1px solid var(--bd)', borderRadius: 8, fontSize: 12 }}
            labelStyle={{ color: 'var(--brand-dark)', fontWeight: 600 }}
          />
          <Line type="monotone" dataKey="Idéal" stroke="var(--tx3)" strokeDasharray="5 3" dot={false} strokeWidth={1.5} />
          <Line type="monotone" dataKey="Réel" stroke="var(--brand-orange)" dot={{ r: 3, fill: 'var(--brand-orange)' }} strokeWidth={2} connectNulls={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  )
}

// ============================================================================
//  PROJECT BOARD PAGE (Kanban)
// ============================================================================

export function ProjectBoardPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const addNotification = useNotificationStore((s) => s.addNotification)
  const { user } = useAuthStore()

  const [activeSprint, setActiveSprint] = useState<string | undefined>(undefined)
  const [showSprintModal, setShowSprintModal] = useState(false)
  const [sprintForm, setSprintForm] = useState({ name: '', goal: '', startDate: '', endDate: '' })
  const [showTaskModal, setShowTaskModal] = useState<{ statusId: string } | null>(null)
  const [showBurndown, setShowBurndown] = useState(false)
  const [editingTask, setEditingTask] = useState<Task | null>(null)
  const [dragTask, setDragTask] = useState<string | null>(null)
  const [dragOver, setDragOver] = useState<string | null>(null)

  const { data: project } = useQuery({
    queryKey: ['project', id],
    queryFn: () => projectsApi.get(id!),
    enabled: !!id,
  })

  const { data: statusesData } = useQuery({
    queryKey: ['project-statuses', id],
    queryFn: () => projectsApi.getStatuses(id!),
    enabled: !!id,
  })

  const { data: tasksData, isLoading: tasksLoading } = useQuery({
    queryKey: ['project-tasks', id, activeSprint],
    queryFn: () => projectsApi.getTasks(id!, { sprintId: activeSprint }),
    enabled: !!id,
  })

  const { data: sprintsData } = useQuery({
    queryKey: ['project-sprints', id],
    queryFn: () => projectsApi.getSprints(id!),
    enabled: !!id,
  })

  const moveTaskMutation = useMutation({
    mutationFn: ({
      taskId,
      statusId,
      position,
    }: {
      taskId: string
      statusId: string
      position: number
    }) => projectsApi.moveTask(id!, taskId, { statusId, position }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['project-tasks', id] }),
  })

  const statuses: TaskStatus[] = statusesData ?? []
  const tasks: Task[] =
    tasksData?.data ?? tasksData?.tasks ?? (Array.isArray(tasksData) ? tasksData : [])
  const sprints: Sprint[] =
    sprintsData?.data ?? sprintsData?.sprints ?? (Array.isArray(sprintsData) ? sprintsData : [])
  const activeSp = sprints.find((s) => s.status === 'ACTIVE')

  const createSprintMutation = useMutation({
    mutationFn: (data: { name: string; goal?: string; startDate?: string; endDate?: string }) =>
      projectsApi.createSprint(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-sprints', id] })
      setShowSprintModal(false)
      setSprintForm({ name: '', goal: '', startDate: '', endDate: '' })
    },
  })
  const startSprintMutation = useMutation({
    mutationFn: (sprintId: string) => projectsApi.startSprint(id!, sprintId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['project-sprints', id] }),
  })
  const closeSprintMutation = useMutation({
    mutationFn: (sprintId: string) => projectsApi.closeSprint(id!, sprintId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['project-sprints', id] }),
  })

  // Grouper les tâches par statut
  const tasksByStatus = statuses.reduce<Record<string, Task[]>>((acc, s) => {
    acc[s.id] = tasks.filter((t) => t.statusId === s.id).sort((a, b) => a.position - b.position)
    return acc
  }, {})

  // ── Drag & Drop ────────────────────────────────────────────────────────────
  const handleDragStart = (taskId: string) => setDragTask(taskId)
  const handleDragOver = (e: React.DragEvent, statusId: string) => {
    e.preventDefault()
    setDragOver(statusId)
  }
  const handleDrop = (e: React.DragEvent, statusId: string) => {
    e.preventDefault()
    if (!dragTask || !statusId) return
    const tasksInCol = tasksByStatus[statusId] ?? []
    moveTaskMutation.mutate({ taskId: dragTask, statusId, position: tasksInCol.length })
    setDragTask(null)
    setDragOver(null)
  }

  if (!project)
    return <KanbanSkeleton cols={4} />

  return (
    <div className="flex flex-col h-full space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between shrink-0">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/projects')}
            className="p-2 hover:bg-[var(--surface-2)] rounded-lg transition-colors"
          >
            <ChevronLeft className="w-5 h-5 text-[var(--tx3)]" />
          </button>
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center"
            style={{ background: `${project.color ?? '#3b82f6'}20` }}
          >
            <FolderKanban className="w-4 h-4" style={{ color: project.color ?? '#3b82f6' }} />
          </div>
          <div>
            <h1 className="text-xl font-bold text-[var(--brand-dark)]">{project.name}</h1>
            <div className="flex items-center gap-2 mt-0.5">
              <StatusBadge status={project.status} />
              <span className="text-xs text-[var(--tx3)]">{tasks.length} tâches</span>
            </div>
          </div>
        </div>

        {/* Sprint selector + gestion */}
        <div className="flex items-center gap-3 flex-wrap">
          {/* Dropdown filtre sprint */}
          {sprints.length > 0 && (
            <select
              value={activeSprint ?? ''}
              onChange={(e) => setActiveSprint(e.target.value || undefined)}
              className="px-3 py-1.5 bg-white border border-[var(--bd)] rounded-lg text-sm text-[var(--brand-dark)] focus:outline-none focus:ring-1 focus:ring-[var(--brand-orange)]"
            >
              <option value="">Toutes les tâches</option>
              {sprints.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}{' '}
                  {s.status === 'ACTIVE'
                    ? '▶ Actif'
                    : s.status === 'COMPLETED'
                      ? '✓ Terminé'
                      : '○ Planifié'}
                </option>
              ))}
            </select>
          )}

          {/* Bouton créer sprint */}
          <button
            onClick={() => setShowSprintModal(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 border border-[var(--bd)] hover:border-[var(--blue)] rounded-lg text-xs text-[var(--tx3)] hover:text-[var(--brand-dark)] transition-colors"
          >
            <Plus className="w-3.5 h-3.5" /> Sprint
          </button>

          {/* Boutons Démarrer / Terminer sprint sélectionné */}
          {activeSprint &&
            (() => {
              const sp = sprints.find((s) => s.id === activeSprint)
              if (!sp) return null
              return (
                <div className="flex items-center gap-2">
                  {sp.status === 'PLANNED' && (
                    <button
                      onClick={() => startSprintMutation.mutate(sp.id)}
                      disabled={!!activeSp}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-[#059669] hover:bg-green-600 disabled:opacity-40 text-white rounded-lg text-xs font-semibold transition-colors"
                      title={activeSp ? 'Un sprint est déjà actif' : 'Démarrer ce sprint'}
                    >
                      <Play className="w-3 h-3" /> Démarrer
                    </button>
                  )}
                  {sp.status === 'ACTIVE' && (
                    <button
                      onClick={() => {
                        if (window.confirm(`Terminer le sprint "${sp.name}" ?`))
                          closeSprintMutation.mutate(sp.id)
                      }}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-[#D97706] hover:bg-amber-600 text-white rounded-lg text-xs font-semibold transition-colors"
                    >
                      <Square className="w-3 h-3" /> Terminer
                    </button>
                  )}
                </div>
              )
            })()}

          {/* Membres */}
          <div className="flex items-center -space-x-2 ml-auto">
            {project.members.slice(0, 4).map((m) => (
              <Avatar key={m.id} user={m.user} size="sm" />
            ))}
          </div>
        </div>
      </div>

      {/* Sprint banner si actif + burndown */}
      {activeSp && (
        <div className="shrink-0 space-y-3">
          <div className="flex items-center gap-3 px-4 py-2.5 bg-[#E0F2FE] border border-[rgba(245,124,0,0.25)] rounded-xl">
            <Play className="w-4 h-4 text-[var(--blue-d)]" />
            <span className="text-sm text-blue-300 font-medium">Sprint actif : {activeSp.name}</span>
            {activeSp.endDate && (
              <span className="text-xs text-[var(--blue-d)]/70">
                Fin : {fmtDate(activeSp.endDate)}
              </span>
            )}
            <button
              onClick={() => setShowBurndown(v => !v)}
              className="ml-auto flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-[var(--blue-d)]/10 hover:bg-[var(--blue-d)]/20 text-[var(--blue-d)] text-xs font-medium transition-colors"
            >
              <TrendingDown className="w-3.5 h-3.5" />
              {showBurndown ? 'Masquer' : 'Burndown'}
            </button>
          </div>

          {showBurndown && activeSp.startDate && activeSp.endDate && (
            <BurndownChart projectId={id!} sprintId={activeSp.id} sprintName={activeSp.name} />
          )}
          {showBurndown && (!activeSp.startDate || !activeSp.endDate) && (
            <div className="px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-700">
              Ce sprint n'a pas de dates de début et fin — impossible d'afficher le burndown.
            </div>
          )}
        </div>
      )}

      {/* Kanban Board */}
      <div className="flex gap-4 overflow-x-auto pb-4 flex-1 min-h-0">
        {statuses.map((status) => {
          const colTasks = tasksByStatus[status.id] ?? []
          const isDragTarget = dragOver === status.id

          return (
            <div
              key={status.id}
              className={`flex-shrink-0 w-72 flex flex-col rounded-xl border transition-colors ${isDragTarget ? 'border-blue-500/50 bg-[var(--blue)]/5' : 'border-[var(--bd)] bg-[var(--bg)]'}`}
              onDragOver={(e) => handleDragOver(e, status.id)}
              onDrop={(e) => handleDrop(e, status.id)}
            >
              {/* Column header */}
              <div className="flex items-center justify-between p-3 border-b border-[var(--bd)]">
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full" style={{ background: status.color }} />
                  <span className="text-sm font-medium text-[var(--brand-dark)]">{status.name}</span>
                  <span className="text-xs text-[var(--tx3)] bg-[#F4F2FB] px-1.5 py-0.5 rounded-full">
                    {colTasks.length}
                  </span>
                </div>
                <button
                  onClick={() => setShowTaskModal({ statusId: status.id })}
                  className="p-1 hover:bg-[var(--bd)] rounded-lg transition-colors text-[var(--tx3)] hover:text-[var(--brand-dark)]"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>

              {/* Tasks */}
              <div className="flex-1 overflow-y-auto p-2 space-y-2 min-h-[200px]">
                {colTasks.map((task) => (
                  <TaskCard
                    key={task.id}
                    task={task}
                    onEdit={() => setEditingTask(task)}
                    onDragStart={() => handleDragStart(task.id)}
                    projectId={id!}
                    queryClient={queryClient}
                  />
                ))}
                {colTasks.length === 0 && !tasksLoading && (
                  <div className="flex flex-col items-center justify-center h-24 text-[var(--tx3)] text-xs">
                    <p>Déposer ici</p>
                  </div>
                )}
              </div>

              {/* Add task */}
              <div className="p-2 border-t border-[var(--bd)]">
                <button
                  onClick={() => setShowTaskModal({ statusId: status.id })}
                  className="w-full flex items-center gap-2 px-3 py-2 text-xs text-[var(--tx3)] hover:text-[var(--brand-dark)] hover:bg-[var(--surface-2)] rounded-lg transition-colors"
                >
                  <Plus className="w-3.5 h-3.5" /> Ajouter une tâche
                </button>
              </div>
            </div>
          )
        })}
      </div>

      {/* Modal création sprint */}
      {showSprintModal && (
        <div className="fixed inset-0 bg-[rgba(13,8,32,0.4)] backdrop-blur-sm z-[99999] flex items-center justify-center p-4">
          <div
            className="bg-white border border-[var(--bd)] rounded-2xl w-full max-w-md p-6 space-y-4"
            style={{ boxShadow: '0 24px 64px rgba(13,8,32,0.18)' }}
          >
            <div className="flex items-center justify-between">
              <h3 className="text-base font-bold text-[var(--brand-dark)]">Nouveau sprint</h3>
              <button
                onClick={() => setShowSprintModal(false)}
                className="text-[var(--tx3)] hover:text-[var(--brand-dark)]"
              >
                ✕
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-[var(--tx3)] mb-1">
                  Nom du sprint *
                </label>
                <input
                  value={sprintForm.name}
                  onChange={(e) => setSprintForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="Sprint 1"
                  className="w-full px-3 py-2 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm focus:outline-none focus:border-[var(--blue)]"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-[var(--tx3)] mb-1">Objectif</label>
                <input
                  value={sprintForm.goal}
                  onChange={(e) => setSprintForm((f) => ({ ...f, goal: e.target.value }))}
                  placeholder="Objectif du sprint"
                  className="w-full px-3 py-2 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm focus:outline-none focus:border-[var(--blue)]"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-[var(--tx3)] mb-1">
                    Date début
                  </label>
                  <input
                    type="date"
                    value={sprintForm.startDate}
                    onChange={(e) => setSprintForm((f) => ({ ...f, startDate: e.target.value }))}
                    className="w-full px-3 py-2 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm focus:outline-none focus:border-[var(--blue)]"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[var(--tx3)] mb-1">Date fin</label>
                  <input
                    type="date"
                    value={sprintForm.endDate}
                    onChange={(e) => setSprintForm((f) => ({ ...f, endDate: e.target.value }))}
                    className="w-full px-3 py-2 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm focus:outline-none focus:border-[var(--blue)]"
                  />
                </div>
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <button
                onClick={() => setShowSprintModal(false)}
                className="flex-1 px-4 py-2 border border-[var(--bd)] rounded-xl text-sm text-[var(--tx3)]"
              >
                Annuler
              </button>
              <button
                onClick={() =>
                  createSprintMutation.mutate({
                    name: sprintForm.name,
                    goal: sprintForm.goal || undefined,
                    startDate: sprintForm.startDate || undefined,
                    endDate: sprintForm.endDate || undefined,
                  })
                }
                disabled={!sprintForm.name || createSprintMutation.isPending}
                className="flex-1 px-4 py-2 bg-[var(--blue)] hover:bg-[var(--blue-d)] disabled:opacity-50 text-white rounded-xl text-sm font-semibold"
              >
                {createSprintMutation.isPending ? 'Création...' : 'Créer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      {showTaskModal && (
        <CreateTaskModal
          projectId={id!}
          statusId={showTaskModal.statusId}
          members={project.members}
          sprints={sprints}
          onClose={() => setShowTaskModal(null)}
          onCreated={() => {
            queryClient.invalidateQueries({ queryKey: ['project-tasks', id] })
            setShowTaskModal(null)
          }}
        />
      )}

      {editingTask && (
        <TaskDetailModal
          task={editingTask}
          projectId={id!}
          members={project.members}
          statuses={statuses}
          onClose={() => setEditingTask(null)}
          onUpdated={() => {
            queryClient.invalidateQueries({ queryKey: ['project-tasks', id] })
            setEditingTask(null)
          }}
        />
      )}
    </div>
  )
}

// ─── Task Card ────────────────────────────────────────────────────────────────

function TaskCard({
  task,
  onEdit,
  onDragStart,
  projectId,
  queryClient,
}: {
  task: Task
  onEdit: () => void
  onDragStart: () => void
  projectId: string
  queryClient: ReturnType<typeof useQueryClient>
}) {
  const isOverdue = task.dueDate && !task.completedAt && new Date(task.dueDate) < new Date()

  return (
    <div
      draggable
      onDragStart={onDragStart}
      onClick={onEdit}
      className={`group bg-[var(--surface-2)]/40 border rounded-lg p-3 cursor-pointer hover:border-blue-500/30 hover:bg-[var(--surface-2)]/60 transition-all ${task.completedAt ? 'border-[rgba(16,185,129,0.25)] opacity-60' : 'border-[var(--bd)]'}`}
    >
      {/* Title */}
      <p
        className={`text-sm font-medium mb-2 line-clamp-2 ${task.completedAt ? 'line-through text-[var(--tx3)]' : 'text-[var(--brand-dark)]'}`}
      >
        {task.title}
      </p>

      {/* Labels */}
      {task.labels.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-2">
          {task.labels.slice(0, 3).map((l) => (
            <span
              key={l}
              className="px-1.5 py-0.5 bg-[var(--blue)]/15 text-[var(--blue-d)] text-[10px] rounded-full border border-[rgba(245,124,0,0.25)]"
            >
              {l}
            </span>
          ))}
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <PriorityIcon priority={task.priority} />
          {task._count && task._count.comments > 0 && (
            <span className="flex items-center gap-0.5 text-[10px] text-[var(--tx3)]">
              <MessageSquare className="w-3 h-3" />
              {task._count.comments}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {task.dueDate && (
            <span
              className={`flex items-center gap-0.5 text-[10px] ${isOverdue ? 'text-[#DC2626]' : 'text-[var(--tx3)]'}`}
            >
              <Clock className="w-3 h-3" />
              {fmtDate(task.dueDate)}
            </span>
          )}
          {task.assignee && <Avatar user={task.assignee} size="xs" />}
        </div>
      </div>

      {/* Drag handle */}
      <GripVertical className="w-3 h-3 text-[var(--tx3)] absolute right-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity" />
    </div>
  )
}

// ─── Create Task Modal ────────────────────────────────────────────────────────

function CreateTaskModal({
  projectId,
  statusId,
  members,
  sprints,
  onClose,
  onCreated,
}: {
  projectId: string
  statusId: string
  members: any[]
  sprints: Sprint[]
  onClose: () => void
  onCreated: () => void
}) {
  const [form, setForm] = useState({
    title: '',
    priority: 'MEDIUM',
    assigneeId: '',
    dueDate: '',
    estimatedHours: '',
    sprintId: '',
  })
  const set = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }))

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      projectsApi.createTask(projectId, {
        ...form,
        statusId,
        assigneeId: form.assigneeId || undefined,
        dueDate: form.dueDate || undefined,
        estimatedHours: form.estimatedHours ? +form.estimatedHours : undefined,
        sprintId: form.sprintId || undefined,
      }),
    onSuccess: onCreated,
  })

  return (
    <div className="fixed inset-0 bg-[rgba(13,8,32,0.50)] backdrop-blur-sm flex items-center justify-center z-[99999] p-4">
      <div className="bg-white border border-[var(--bd)] rounded-2xl w-full max-w-2xl shadow-2xl">
        <div className="flex items-center justify-between p-5 border-b border-[var(--bd)]">
          <h2 className="text-base font-semibold text-[var(--brand-dark)]">Nouvelle tâche</h2>
          <button onClick={onClose}>
            <X className="w-4 h-4 text-[var(--tx3)]" />
          </button>
        </div>
        <div className="p-5 space-y-3">
          <input
            value={form.title}
            onChange={(e) => set('title', e.target.value)}
            placeholder="Titre de la tâche *"
            className="w-full px-3 py-2 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm text-[var(--brand-dark)] focus:outline-none focus:ring-1 focus:ring-[var(--blue)]"
          />
          <div className="grid grid-cols-2 gap-3">
            <select
              value={form.priority}
              onChange={(e) => set('priority', e.target.value)}
              className="px-3 py-2 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm text-[var(--brand-dark)] focus:outline-none focus:ring-1 focus:ring-[var(--blue)]"
            >
              {Object.entries(PRIORITY).map(([k, v]) => (
                <option key={k} value={k}>
                  {v.label}
                </option>
              ))}
            </select>
            <select
              value={form.assigneeId}
              onChange={(e) => set('assigneeId', e.target.value)}
              className="px-3 py-2 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm text-[var(--brand-dark)] focus:outline-none focus:ring-1 focus:ring-[var(--blue)]"
            >
              <option value="">Non assigné</option>
              {members.map((m) => (
                <option key={m.userId} value={m.userId}>
                  {m.user.firstName} {m.user.lastName}
                </option>
              ))}
            </select>
            <input
              type="date"
              value={form.dueDate}
              onChange={(e) => set('dueDate', e.target.value)}
              className="px-3 py-2 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm text-[var(--brand-dark)] focus:outline-none focus:ring-1 focus:ring-[var(--blue)]"
            />
            <input
              type="number"
              value={form.estimatedHours}
              onChange={(e) => set('estimatedHours', e.target.value)}
              placeholder="Estimation (h)"
              className="px-3 py-2 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm text-[var(--brand-dark)] focus:outline-none focus:ring-1 focus:ring-[var(--blue)]"
            />
          </div>
          {sprints.length > 0 && (
            <select
              value={form.sprintId}
              onChange={(e) => set('sprintId', e.target.value)}
              className="w-full px-3 py-2 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm text-[var(--brand-dark)] focus:outline-none focus:ring-1 focus:ring-[var(--blue)]"
            >
              <option value="">Aucun sprint</option>
              {sprints.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          )}
        </div>
        <div className="flex justify-end gap-3 p-5 border-t border-[var(--bd)]">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-[var(--tx3)] hover:text-[var(--brand-dark)]"
          >
            Annuler
          </button>
          <button
            onClick={() => mutate()}
            disabled={isPending || !form.title}
            className="flex items-center gap-2 px-5 py-2 bg-[var(--blue)] hover:bg-[var(--blue-d)] text-white rounded-lg text-sm font-medium disabled:opacity-50"
          >
            {isPending ? <Spinner /> : <Plus className="w-4 h-4" />} Créer
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Task Detail Modal ────────────────────────────────────────────────────────

function TaskDetailModal({
  task,
  projectId,
  members,
  statuses,
  onClose,
  onUpdated,
}: {
  task: Task
  projectId: string
  members: any[]
  statuses: TaskStatus[]
  onClose: () => void
  onUpdated: () => void
}) {
  const [comment, setComment]             = useState('')
  const [activeTab, setActiveTab]         = useState<'details' | 'timelogs' | 'labels'>('details')
  const [logHours, setLogHours]           = useState('')
  const [logDate, setLogDate]             = useState(new Date().toISOString().split('T')[0])
  const [logDesc, setLogDesc]             = useState('')
  const [newLabelName, setNewLabelName]   = useState('')
  const [newLabelColor, setNewLabelColor] = useState('var(--brand-orange)')
  const queryClient = useQueryClient()
  const addNotif = useNotificationStore((s) => s.addNotification)

  const { data: fullTask } = useQuery({
    queryKey: ['task', projectId, task.id],
    queryFn: () => projectsApi.getTask(projectId, task.id),
  })

  const { data: commentsData } = useQuery({
    queryKey: ['task-comments', projectId, task.id],
    queryFn: () => projectsApi.getTaskComments(projectId, task.id),
  })

  const { data: timeLogsData } = useQuery({
    queryKey: ['timelogs', projectId, task.id],
    queryFn: () => projectsApi.getTimeLogs(projectId, task.id),
  })

  const { data: labelsData } = useQuery({
    queryKey: ['project-labels', projectId],
    queryFn: () => projectsApi.getLabels(projectId),
  })

  const updateMutation = useMutation({
    mutationFn: (data: any) => projectsApi.updateTask(projectId, task.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['task', projectId, task.id] })
      onUpdated()
    },
  })

  const commentMutation = useMutation({
    mutationFn: () => projectsApi.addTaskComment(projectId, task.id, comment),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['task-comments', projectId, task.id] })
      setComment('')
    },
  })

  const addLogMutation = useMutation({
    mutationFn: () => projectsApi.addTimeLog(projectId, task.id, {
      minutes: Math.round(parseFloat(logHours) * 60),
      date: logDate,
      description: logDesc || undefined,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['timelogs', projectId, task.id] })
      queryClient.invalidateQueries({ queryKey: ['task', projectId, task.id] })
      setLogHours('')
      setLogDesc('')
      addNotif({ id: Date.now().toString(), type: 'success', title: 'Temps enregistré' })
    },
    onError: (e: any) => addNotif({ id: Date.now().toString(), type: 'error', title: e.response?.data?.message ?? 'Erreur' }),
  })

  const deleteLogMutation = useMutation({
    mutationFn: (logId: string) => projectsApi.deleteTimeLog(projectId, logId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['timelogs', projectId, task.id] })
      queryClient.invalidateQueries({ queryKey: ['task', projectId, task.id] })
    },
  })

  const createLabelMutation = useMutation({
    mutationFn: () => projectsApi.createLabel(projectId, { name: newLabelName.trim(), color: newLabelColor }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-labels', projectId] })
      setNewLabelName('')
    },
    onError: (e: any) => addNotif({ id: Date.now().toString(), type: 'error', title: e.response?.data?.message ?? 'Erreur' }),
  })

  const deleteLabelMutation = useMutation({
    mutationFn: (labelId: string) => projectsApi.deleteLabel(projectId, labelId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['project-labels', projectId] }),
  })

  const toggleLabelMutation = useMutation({
    mutationFn: (labelId: string) => {
      const current: string[] = t.labels ?? []
      const next = current.includes(labelId)
        ? current.filter((id: string) => id !== labelId)
        : [...current, labelId]
      return projectsApi.updateTask(projectId, task.id, { labels: next })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['task', projectId, task.id] })
      onUpdated()
    },
  })

  const t = fullTask ?? task
  const comments = commentsData?.comments ?? []
  const logs: any[] = timeLogsData?.data ?? []
  const totalMinutes: number = timeLogsData?.totalMinutes ?? 0
  const totalH = (totalMinutes / 60).toFixed(1)
  const projectLabels: ProjectLabel[] = labelsData ?? []
  const taskLabelIds: string[] = t.labels ?? []

  return (
    <div className="fixed inset-0 bg-[rgba(13,8,32,0.50)] backdrop-blur-sm flex items-center justify-center z-[99999] p-4">
      <div className="bg-white border border-[var(--bd)] rounded-2xl w-full max-w-2xl max-h-[85vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-start justify-between p-5 border-b border-[var(--bd)]">
          <div className="flex-1 pr-4">
            <p className="text-lg font-semibold text-[var(--brand-dark)] leading-tight">{t.title}</p>
            <div className="flex items-center gap-2 mt-1.5">
              <PriorityIcon priority={t.priority} />
              <span className={`text-xs ${PRIORITY[t.priority]?.color}`}>
                {PRIORITY[t.priority]?.label}
              </span>
              {t.completedAt && (
                <span className="flex items-center gap-1 text-xs text-[#059669]">
                  <CheckCircle className="w-3 h-3" /> Terminé
                </span>
              )}
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-[var(--bd)] rounded-lg">
            <X className="w-4 h-4 text-[var(--tx3)]" />
          </button>
        </div>

        {/* Onglets */}
        <div className="flex border-b border-[var(--bd)] px-5 shrink-0">
          {([
            { key: 'details',  label: 'Détails' },
            { key: 'timelogs', label: `Temps (${totalH}h)` },
            { key: 'labels',   label: `Labels (${projectLabels.length})` },
          ] as const).map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-2.5 text-xs font-medium border-b-2 transition-colors ${
                activeTab === tab.key
                  ? 'border-[var(--blue)] text-[var(--brand-dark)]'
                  : 'border-transparent text-[var(--tx3)] hover:text-[var(--brand-dark)]'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Body scrollable */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5">

          {/* ── ONGLET DÉTAILS ─────────────────────────────────────── */}
          {activeTab === 'details' && (<>
            {/* Infos rapides */}
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-[var(--surface-2)]/30 rounded-lg p-3">
                <p className="text-xs text-[var(--tx3)] mb-1">Statut</p>
                <select
                  value={t.statusId}
                  onChange={(e) => updateMutation.mutate({ statusId: e.target.value })}
                  className="w-full bg-transparent text-sm text-[var(--brand-dark)] focus:outline-none"
                >
                  {statuses.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>
              <div className="bg-[var(--surface-2)]/30 rounded-lg p-3">
                <p className="text-xs text-[var(--tx3)] mb-1">Assigné à</p>
                <select
                  value={t.assigneeId ?? ''}
                  onChange={(e) => updateMutation.mutate({ assigneeId: e.target.value || undefined })}
                  className="w-full bg-transparent text-sm text-[var(--brand-dark)] focus:outline-none"
                >
                  <option value="">Non assigné</option>
                  {members.map((m) => (
                    <option key={m.userId} value={m.userId}>
                      {m.user.firstName} {m.user.lastName}
                    </option>
                  ))}
                </select>
              </div>
              {t.dueDate && (
                <div className="bg-[var(--surface-2)]/30 rounded-lg p-3">
                  <p className="text-xs text-[var(--tx3)] mb-1">Échéance</p>
                  <p className={`text-sm ${new Date(t.dueDate) < new Date() && !t.completedAt ? 'text-[#DC2626]' : 'text-[var(--brand-dark)]'}`}>
                    {fmtFull(t.dueDate)}
                  </p>
                </div>
              )}
              {(t.estimatedHours || parseFloat(totalH) > 0) && (
                <div className="bg-[var(--surface-2)]/30 rounded-lg p-3">
                  <p className="text-xs text-[var(--tx3)] mb-1">Temps saisi</p>
                  <p className="text-sm text-[var(--brand-dark)]">
                    {totalH}h {t.estimatedHours ? `/ ${t.estimatedHours}h estimé` : ''}
                  </p>
                </div>
              )}
            </div>

            {/* Labels assignés */}
            {taskLabelIds.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {taskLabelIds.map((lid) => {
                  const lbl = projectLabels.find(l => l.id === lid)
                  if (!lbl) return null
                  return (
                    <span key={lid} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium text-white" style={{ background: lbl.color }}>
                      {lbl.name}
                    </span>
                  )
                })}
              </div>
            )}

            {/* Description */}
            {t.description && (
              <div>
                <p className="text-xs font-medium text-[var(--tx3)] mb-2">Description</p>
                <p className="text-sm text-[var(--tx2)] whitespace-pre-wrap">{t.description}</p>
              </div>
            )}

            {/* Commentaires */}
            <div>
              <p className="text-xs font-medium text-[var(--tx3)] mb-3">
                Commentaires ({comments.length})
              </p>
              <div className="space-y-3 mb-3">
                {comments.map((c: any) => (
                  <div key={c.id} className="flex gap-3">
                    <Avatar user={c.author} size="xs" />
                    <div className="flex-1 bg-[var(--surface-2)]/30 rounded-lg p-3">
                      <p className="text-xs font-medium text-[var(--brand-dark)] mb-1">
                        {c.author?.firstName} {c.author?.lastName}
                      </p>
                      <p className="text-sm text-[var(--tx2)]">{c.content}</p>
                      <p className="text-[10px] text-[var(--tx3)] mt-1">{fmtFull(c.createdAt)}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <input
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Ajouter un commentaire…"
                  onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && comment && commentMutation.mutate()}
                  className="flex-1 px-3 py-2 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm text-[var(--brand-dark)] focus:outline-none focus:ring-1 focus:ring-[var(--blue)]"
                />
                <button
                  onClick={() => comment && commentMutation.mutate()}
                  disabled={!comment || commentMutation.isPending}
                  className="px-3 py-2 bg-[var(--blue)] hover:bg-[var(--blue-d)] text-white rounded-lg text-sm disabled:opacity-50"
                >
                  Envoyer
                </button>
              </div>
            </div>
          </>)}

          {/* ── ONGLET TEMPS ───────────────────────────────────────── */}
          {activeTab === 'timelogs' && (<>
            {/* Formulaire saisie */}
            <div className="bg-[var(--surface-2)] border border-[var(--bd)] rounded-xl p-4 space-y-3">
              <p className="text-xs font-semibold text-[var(--brand-dark)]">Saisir du temps</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] text-[var(--tx3)] mb-1 block">Heures *</label>
                  <input
                    type="number"
                    min="0.25"
                    max="24"
                    step="0.25"
                    value={logHours}
                    onChange={(e) => setLogHours(e.target.value)}
                    placeholder="ex: 1.5"
                    className="w-full px-3 py-2 bg-white border border-[var(--bd)] rounded-lg text-sm text-[var(--brand-dark)] focus:outline-none focus:ring-1 focus:ring-[var(--brand-orange)]"
                  />
                </div>
                <div>
                  <label className="text-[11px] text-[var(--tx3)] mb-1 block">Date *</label>
                  <input
                    type="date"
                    value={logDate}
                    onChange={(e) => setLogDate(e.target.value)}
                    className="w-full px-3 py-2 bg-white border border-[var(--bd)] rounded-lg text-sm text-[var(--brand-dark)] focus:outline-none focus:ring-1 focus:ring-[var(--brand-orange)]"
                  />
                </div>
              </div>
              <div>
                <label className="text-[11px] text-[var(--tx3)] mb-1 block">Description (optionnel)</label>
                <input
                  value={logDesc}
                  onChange={(e) => setLogDesc(e.target.value)}
                  placeholder="Ex: Implémentation du service…"
                  className="w-full px-3 py-2 bg-white border border-[var(--bd)] rounded-lg text-sm text-[var(--brand-dark)] focus:outline-none focus:ring-1 focus:ring-[var(--brand-orange)]"
                />
              </div>
              <button
                onClick={() => parseFloat(logHours) > 0 && logDate && addLogMutation.mutate()}
                disabled={!logHours || !logDate || addLogMutation.isPending}
                className="w-full py-2 bg-[var(--blue)] hover:bg-[var(--blue-d)] text-white rounded-lg text-sm font-medium disabled:opacity-40 transition-colors"
              >
                {addLogMutation.isPending ? 'Enregistrement…' : 'Enregistrer le temps'}
              </button>
            </div>

            {/* Total */}
            <div className="flex items-center justify-between text-sm">
              <span className="text-[var(--tx3)]">Total saisi</span>
              <span className="font-semibold text-[var(--brand-dark)]">{totalH}h</span>
            </div>

            {/* Liste des logs */}
            {logs.length === 0 ? (
              <p className="text-center text-sm text-[var(--tx3)] py-6">Aucun temps enregistré</p>
            ) : (
              <div className="space-y-2">
                {logs.map((log: any) => (
                  <div key={log.id} className="flex items-center justify-between bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg px-3 py-2.5">
                    <div className="flex items-center gap-2.5">
                      <Avatar user={log.user} size="xs" />
                      <div>
                        <p className="text-xs font-medium text-[var(--brand-dark)]">
                          {((log.minutes ?? 0) / 60).toFixed(1)}h — {fmtDate(log.date ?? log.logDate)}
                        </p>
                        {log.description && (
                          <p className="text-[11px] text-[var(--tx3)]">{log.description}</p>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        if (window.confirm('Supprimer ce log ?')) deleteLogMutation.mutate(log.id)
                      }}
                      className="p-1.5 hover:bg-[#FEE2E2] rounded-lg text-[var(--tx3)] hover:text-[#DC2626] transition-colors"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </>)}

          {/* ── ONGLET LABELS ──────────────────────────────────────── */}
          {activeTab === 'labels' && (<>
            {/* Labels assignés à cette tâche */}
            <div>
              <p className="text-xs font-semibold text-[var(--brand-dark)] mb-3">Labels de ce projet</p>
              {projectLabels.length === 0 ? (
                <p className="text-sm text-[var(--tx3)]">Aucun label créé pour ce projet</p>
              ) : (
                <div className="space-y-2">
                  {projectLabels.map((lbl) => {
                    const isAssigned = taskLabelIds.includes(lbl.id)
                    return (
                      <div key={lbl.id} className="flex items-center justify-between">
                        <button
                          onClick={() => toggleLabelMutation.mutate(lbl.id)}
                          className={`flex items-center gap-2.5 flex-1 px-3 py-2 rounded-lg border transition-all text-left ${
                            isAssigned
                              ? 'border-[var(--blue)]/40 bg-[var(--blue)]/5'
                              : 'border-[var(--bd)] hover:border-[var(--tx3)]'
                          }`}
                        >
                          <span className="w-3 h-3 rounded-full shrink-0" style={{ background: lbl.color }} />
                          <span className="text-sm text-[var(--brand-dark)]">{lbl.name}</span>
                          {isAssigned && (
                            <CheckCircle2 className="w-3.5 h-3.5 text-[var(--blue)] ml-auto" />
                          )}
                        </button>
                        <button
                          onClick={() => {
                            if (window.confirm(`Supprimer le label "${lbl.name}" ?`))
                              deleteLabelMutation.mutate(lbl.id)
                          }}
                          className="ml-2 p-1.5 hover:bg-[#FEE2E2] rounded-lg text-[var(--tx3)] hover:text-[#DC2626] transition-colors"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>

            {/* Créer un nouveau label */}
            <div className="border-t border-[var(--bd)] pt-4">
              <p className="text-xs font-semibold text-[var(--brand-dark)] mb-3">Créer un label</p>
              <div className="flex gap-2">
                <input
                  type="color"
                  value={newLabelColor}
                  onChange={(e) => setNewLabelColor(e.target.value)}
                  className="w-9 h-9 rounded-lg border border-[var(--bd)] cursor-pointer p-0.5"
                />
                <input
                  value={newLabelName}
                  onChange={(e) => setNewLabelName(e.target.value)}
                  placeholder="Nom du label…"
                  onKeyDown={(e) => e.key === 'Enter' && newLabelName.trim() && createLabelMutation.mutate()}
                  className="flex-1 px-3 py-2 bg-white border border-[var(--bd)] rounded-lg text-sm text-[var(--brand-dark)] focus:outline-none focus:ring-1 focus:ring-[var(--brand-orange)]"
                />
                <button
                  onClick={() => newLabelName.trim() && createLabelMutation.mutate()}
                  disabled={!newLabelName.trim() || createLabelMutation.isPending}
                  className="px-3 py-2 bg-[var(--blue)] hover:bg-[var(--blue-d)] text-white rounded-lg text-sm disabled:opacity-40"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>
          </>)}

        </div>
      </div>
    </div>
  )
}

// ─── ProjectDetailPage ────────────────────────────────────────────────────────
export function ProjectDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [showAddMember, setShowAddMember] = useState(false)
  const [memberEmail, setMemberEmail] = useState('')
  const { data: project, isLoading } = useQuery({
    queryKey: ['project', id],
    queryFn: () => projectsApi.get(id!),
    enabled: !!id,
  })
  const { data: allUsers = [] } = useQuery({
    queryKey: ['all-users-project'],
    queryFn: () => usersApi.directory({ limit: 100 }),
  })
  const addMemberMutation = useMutation({
    mutationFn: (userId: string) => projectsApi.addMember(id!, { userId, role: 'MEMBER' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['project', id] })
      setShowAddMember(false)
      setMemberEmail('')
    },
  })

  if (isLoading)
    return <ListPageSkeleton cols={4} rows={5} />
  if (!project) return <div className="p-6 text-[var(--tx3)]">Projet introuvable.</div>

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/projects')}
            className="text-[var(--tx3)] hover:text-[var(--brand-dark)]"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center text-[var(--brand-dark)] font-bold text-sm shrink-0"
            style={{ backgroundColor: project.color ?? '#3b82f6' }}
          >
            {project.name.slice(0, 2).toUpperCase()}
          </div>
          <div>
            <h1 className="text-xl font-bold text-[var(--brand-dark)]">{project.name}</h1>
            <p className="text-sm text-[var(--tx3)]">{project.description ?? 'Aucune description'}</p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate(`/projects/${id}/board`)}
            className="flex items-center gap-2 px-3 py-1.5 bg-white hover:bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm text-[var(--tx2)] transition-colors"
          >
            <FolderKanban className="w-4 h-4" />
            Board
          </button>
          <button
            onClick={() => navigate(`/projects/${id}/gantt`)}
            className="flex items-center gap-2 px-3 py-1.5 bg-[var(--blue)] hover:bg-[var(--blue-d)] rounded-lg text-sm text-white transition-colors"
          >
            <svg className="w-4 h-4" viewBox="0 0 16 16" fill="none">
              <rect x="1" y="3" width="6" height="2.5" rx="1" fill="currentColor" opacity="0.9" />
              <rect x="4" y="6.5" width="8" height="2.5" rx="1" fill="currentColor" opacity="0.7" />
              <rect x="2" y="10" width="10" height="2.5" rx="1" fill="currentColor" opacity="0.5" />
            </svg>
            Gantt
          </button>
          <button
            onClick={async () => {
              try {
                const resp = await api.get(`/projects/${id}/export/excel`, { responseType: 'blob' })
                const blob = new Blob([resp.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
                const link = document.createElement('a')
                link.href = URL.createObjectURL(blob)
                const disposition = resp.headers['content-disposition'] ?? ''
                const match = disposition.match(/filename="?(.+?)"?$/)
                link.download = match?.[1] ?? `Suivi_Taches_${id}.xlsx`
                link.click()
                URL.revokeObjectURL(link.href)
              } catch { /* ignore */ }
            }}
            className="flex items-center gap-2 px-3 py-1.5 bg-green-600 hover:bg-green-700 rounded-lg text-sm text-white transition-colors"
          >
            <svg className="w-4 h-4" viewBox="0 0 16 16" fill="none"><path d="M3 14h10M8 2v9m0 0l3-3m-3 3L5 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
            Export Excel
          </button>
        </div>
      </div>

      {/* Infos projet */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { label: 'Statut', value: project.status },
          { label: 'Priorité', value: project.priority },
          { label: 'Membres', value: project._count.members },
          { label: 'Tâches', value: project._count.tasks },
        ].map((kpi) => (
          <div key={kpi.label} className="bg-white border border-[var(--bd)] rounded-xl p-4">
            <p className="text-xs text-[var(--tx3)]">{kpi.label}</p>
            <p className="text-lg font-bold text-[var(--brand-dark)] mt-1">{kpi.value}</p>
          </div>
        ))}
      </div>

      {/* Membres */}
      <div className="bg-white border border-[var(--bd)] rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold text-[var(--brand-dark)]">
            Membres ({project.members?.length ?? 0})
          </h2>
          <button
            onClick={() => setShowAddMember(true)}
            className="flex items-center gap-1 px-3 py-1.5 bg-[var(--blue)] hover:bg-[var(--blue-d)] text-white rounded-lg text-xs font-semibold transition-colors"
          >
            <Plus className="w-3.5 h-3.5" /> Ajouter
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          {project.members?.map((m) => (
            <div key={m.id} className="flex items-center gap-2 bg-[var(--surface-2)] rounded-lg px-3 py-1.5">
              <div className="w-6 h-6 rounded-full bg-gradient-to-br from-primary-600 to-primary-500 flex items-center justify-center text-[var(--brand-dark)] text-[9px] font-bold">
                {m.user.firstName[0]}
                {m.user.lastName[0]}
              </div>
              <span className="text-xs text-[var(--tx2)]">
                {m.user.firstName} {m.user.lastName}
              </span>
              <span className="text-[10px] text-[var(--tx3)]">{m.role}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Modal ajout membre */}
      {showAddMember && (
        <div className="fixed inset-0 bg-[rgba(13,8,32,0.4)] backdrop-blur-sm z-[99999] flex items-center justify-center p-4">
          <div
            className="bg-white border border-[var(--bd)] rounded-2xl w-full max-w-md p-6 space-y-4"
            style={{ boxShadow: '0 24px 64px rgba(13,8,32,0.18)' }}
          >
            <div className="flex items-center justify-between">
              <h3 className="text-base font-bold text-[var(--brand-dark)]">Ajouter un membre</h3>
              <button
                onClick={() => setShowAddMember(false)}
                className="text-[var(--tx3)] hover:text-[var(--brand-dark)]"
              >
                ✕
              </button>
            </div>
            <input
              value={memberEmail}
              onChange={(e) => setMemberEmail(e.target.value)}
              placeholder="Rechercher un utilisateur..."
              className="w-full px-3 py-2 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm focus:outline-none focus:border-[var(--blue)]"
            />
            <div className="max-h-48 overflow-y-auto space-y-1">
              {(allUsers as any[])
                .filter(
                  (u: any) =>
                    !memberEmail ||
                    `${u.firstName} ${u.lastName} ${u.email}`
                      .toLowerCase()
                      .includes(memberEmail.toLowerCase()),
                )
                .filter((u: any) => !project.members?.find((m) => m.userId === u.id))
                .map((u: any) => (
                  <button
                    key={u.id}
                    onClick={() => addMemberMutation.mutate(u.id)}
                    className="w-full flex items-center gap-3 px-3 py-2 hover:bg-[var(--surface-2)] rounded-lg transition-colors text-left"
                  >
                    <div className="w-7 h-7 rounded-full bg-gradient-to-br from-primary-600 to-primary-500 flex items-center justify-center text-[var(--brand-dark)] text-[10px] font-bold flex-shrink-0">
                      {u.firstName?.[0]}
                      {u.lastName?.[0]}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-[var(--brand-dark)]">
                        {u.firstName} {u.lastName}
                      </p>
                      <p className="text-xs text-[var(--tx3)]">{u.email}</p>
                    </div>
                  </button>
                ))}
            </div>
            <button
              onClick={() => setShowAddMember(false)}
              className="w-full px-4 py-2 border border-[var(--bd)] rounded-xl text-sm text-[var(--tx3)]"
            >
              Fermer
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
